let bundleDebounceTimeout = null;
// ============================
        // GLOBAL CACHE MANAGER WITH LOCALSTORAGE (UPDATED WITH API CONSOLIDATION)
        // ============================
        class VehicleCacheManager {
            static API_BASE = document.querySelector('html').dataset.devMode === 'true' ? 'https://carbon-develop.clients.graduatesapi.com/api/catalog' : 'https://carbon.clients.graduatesapi.com/api/catalog';
            static PLATE_API_BASE = document.querySelector('html').dataset.devMode === 'true' ? 'https://carbon-develop.clients.graduatesapi.com/api/plate' : 'https://carbon.clients.graduatesapi.com/api/plate';
            
            static STORAGE_KEYS = {
                MAKES: 'carbonaccents_makes',
                MODELS: 'carbonaccents_models',
                GENERATIONS: 'carbonaccents_generations', 
                VARIANTS: 'carbonaccents_variants',
                BODY_TYPES: 'carbonaccents_body_types',
                PLATE_RESULTS: 'carbonaccents_plate_results',
                LAST_VEHICLE: 'carbonaccents_last_vehicle',
                SAVED_DATA: 'carbonaccents_saved_data',
                GLOBAL_BUNDLE:'global_bundle' 
            };
            
            static CACHE_EXPIRY = {
                MAKES: 24 * 60 * 60 * 1000, // 24 hours
                HIERARCHY: 6 * 60 * 60 * 1000, // 6 hours for models/generations/variants
                PLATE_RESULTS: 7 * 24 * 60 * 60 * 1000, // 7 days for plate results
                SELECTIONS: 30 * 24 * 60 * 60 * 1000 // 30 days for saved selections
            };

            // API call tracking for consolidation
            static pendingCalls = {};
            static memoryCache = {};

            static saveToStorage(key, data, customExpiry = null) {
                try {
                    const expiry = customExpiry || this.CACHE_EXPIRY.HIERARCHY;
                    const storageData = {
                        data: data,
                        timestamp: Date.now(),
                        expiry: expiry
                    };
                    localStorage.setItem(key, JSON.stringify(storageData));
                    return true;
                } catch (error) {
                    console.warn('Storage not available:', error);
                    return false;
                }
            }

            static loadFromStorage(key) {
                try {
                    const stored = localStorage.getItem(key);
                    if (!stored) return null;
                    
                    const parsed = JSON.parse(stored);
                    const age = Date.now() - parsed.timestamp;
                    
                    if (age > parsed.expiry) {
                        localStorage.removeItem(key);
                        return null;
                    }
                    
                    return parsed.data;
                } catch (error) {
                    console.warn('Storage not available:', error);
                    return null;
                }
            }

            static clearAllStorage() {
                try {
                    Object.values(this.STORAGE_KEYS).forEach(key => {
                        localStorage.removeItem(key);
                    });
                    this.memoryCache = {};
                    this.pendingCalls = {}; // Clear pending calls
                } catch (error) {
                    console.warn('Storage clear failed:', error);
                }
                this.updateGlobalStorageInfo();
            }

            static getCacheKey(baseKey, id = null) {
                return id ? `${baseKey}_${id}` : baseKey;
            }

            static updateGlobalStorageInfo() {
                const globalInfo = document.getElementById('global-storage-info');
                if (!globalInfo) return;
                
                try {
                    const savedData = this.loadSavedData();
                    
                    let info = '📦 Cache: localStorage active';
                    if (savedData) {
                        info += ' | 🚗 Data saved';
                        if (savedData.plateNumber) {
                            info += ` (Plate: ${savedData.plateNumber})`;
                        }
                    }
                    
                    globalInfo.innerHTML = info;
                } catch (error) {
                    globalInfo.innerHTML = '📦 Cache: localStorage error';
                }
            }

            // Consolidated data persistence methods (UPDATED WITH BUNDLE ENABLED)
            static saveData(dataObject) {
                const saveData = {
                    ...dataObject,
                    timestamp: Date.now()
                };
                
                this.saveToStorage(this.STORAGE_KEYS.SAVED_DATA, saveData, this.CACHE_EXPIRY.SELECTIONS);
               
                this.updateGlobalStorageInfo();
            }

            static loadSavedData() {
                return this.loadFromStorage(this.STORAGE_KEYS.SAVED_DATA);
            }

            // Wrapper methods for backward compatibility
            static saveSelection(selectionData) {
                // Keep plate data when manual selection is made
                const existingData = this.loadSavedData();
                let dataToSave = { ...selectionData };
                
                // Preserve plate data from existing data
                if (existingData && existingData.plateNumber) {
                    dataToSave.plateNumber = existingData.plateNumber;
                    dataToSave.plateResult = existingData.plateResult;
                }
                
                this.saveData(dataToSave);
            }

            static savePlateResult(plateNumber, plateResult) {
                const existingData = this.loadSavedData();
                
                const saveData = {
                    plateNumber: plateNumber,
                    plateResult: plateResult,
                    ...(existingData || {}),
                    source: 'plate'
                };
                
                this.saveData(saveData);
            }

            static loadSavedSelection() {
                return this.loadSavedData();
            }

            static loadSavedPlateResult() {
                const savedData = this.loadSavedData();
                if (savedData && savedData.plateNumber) {
                    return {
                        plateNumber: savedData.plateNumber,
                        result: savedData.plateResult,
                        timestamp: savedData.timestamp
                    };
                }
                return null;
            }

            static clearSavedState() {
                try {
                    localStorage.removeItem(this.STORAGE_KEYS.SAVED_DATA);
                    this.updateGlobalStorageInfo();
                } catch (error) {
                    console.warn('Failed to clear saved state:', error);
                }
            }

            // CONSOLIDATED API METHODS (UPDATED FOR SINGLE CALL OPTIMIZATION)
            
            static async loadMakes() {
                const cacheKey = 'makes';
                
                try {
                    // Check if there's already a pending call for this data
                    if (this.pendingCalls[cacheKey]) {
                      
                        return await this.pendingCalls[cacheKey];
                    }
                    
                    // Try memory cache first
                    if (this.memoryCache.makes) {
                     
                        return this.memoryCache.makes;
                    }
                    
                    // Try localStorage
                    const cached = this.loadFromStorage(this.STORAGE_KEYS.MAKES);
                    if (cached) {
                        this.memoryCache.makes = cached;
                       
                        return cached;
                    }
                    
                    // Create the API call promise and store it
                   
                    this.pendingCalls[cacheKey] = this._fetchMakes();
                    
                    try {
                        const result = await this.pendingCalls[cacheKey];
                        return result;
                    } finally {
                        // Clean up the pending call
                        delete this.pendingCalls[cacheKey];
                    }
                    
                } catch (error) {
                    // Clean up pending call on error
                    delete this.pendingCalls[cacheKey];
                    console.error('Error loading makes:', error);
                    throw error;
                }
            }

            static async _fetchMakes() {
                const response = await fetch(`${this.API_BASE}/makes`);
                if (!response.ok) throw new Error('Failed to load makes');
                
                const data = await response.json();
                const makes = data.data || [];
                
                // Cache in both memory and localStorage
                this.memoryCache.makes = makes;
                this.saveToStorage(this.STORAGE_KEYS.MAKES, makes, this.CACHE_EXPIRY.MAKES);
                
              
                return makes;
            }

            static async loadModels(makeId) {
                const cacheKey = `models_${makeId}`;
                const storageKey = this.getCacheKey(this.STORAGE_KEYS.MODELS, makeId);
                
                try {
                    // Check for pending call
                    if (this.pendingCalls[cacheKey]) {
                 
                        return await this.pendingCalls[cacheKey];
                    }
                    
                    // Try memory cache first
                    if (this.memoryCache[cacheKey]) {
                      
                        return this.memoryCache[cacheKey];
                    }
                    
                    // Try localStorage
                    const cached = this.loadFromStorage(storageKey);
                    if (cached) {
                        this.memoryCache[cacheKey] = cached;
                        
                        return cached;
                    }
                    
                    // Create API call promise
                  
                    this.pendingCalls[cacheKey] = this._fetchModels(makeId, cacheKey, storageKey);
                    
                    try {
                        const result = await this.pendingCalls[cacheKey];
                        return result;
                    } finally {
                        delete this.pendingCalls[cacheKey];
                    }
                    
                } catch (error) {
                    delete this.pendingCalls[cacheKey];
                    console.error('Error loading models:', error);
                    throw error;
                }
            }

            static async _fetchModels(makeId, cacheKey, storageKey) {
                const response = await fetch(`${this.API_BASE}/makes/${makeId}/models`);
                if (!response.ok) throw new Error('Failed to load models');
                
                const data = await response.json();
                const models = data.data || [];
                
                this.memoryCache[cacheKey] = models;
                this.saveToStorage(storageKey, models);
                
           
                return models;
            }

            static async loadGenerations(modelId) {
                const cacheKey = `generations_${modelId}`;
                const storageKey = this.getCacheKey(this.STORAGE_KEYS.GENERATIONS, modelId);
                
                try {
                    if (this.pendingCalls[cacheKey]) {
                       
                        return await this.pendingCalls[cacheKey];
                    }
                    
                    if (this.memoryCache[cacheKey]) {
                      
                        return this.memoryCache[cacheKey];
                    }
                    
                    const cached = this.loadFromStorage(storageKey);
                    if (cached) {
                        this.memoryCache[cacheKey] = cached;
                      
                        return cached;
                    }
                    
                   
                    this.pendingCalls[cacheKey] = this._fetchGenerations(modelId, cacheKey, storageKey);
                    
                    try {
                        const result = await this.pendingCalls[cacheKey];
                        return result;
                    } finally {
                        delete this.pendingCalls[cacheKey];
                    }
                    
                } catch (error) {
                    delete this.pendingCalls[cacheKey];
                    console.error('Error loading generations:', error);
                    throw error;
                }
            }

            static async _fetchGenerations(modelId, cacheKey, storageKey) {
                const response = await fetch(`${this.API_BASE}/models/${modelId}/generations`);
                if (!response.ok) throw new Error('Failed to load generations');
                
                const data = await response.json();
                const generations = data.data || [];
                
                this.memoryCache[cacheKey] = generations;
                this.saveToStorage(storageKey, generations);
                
             
                return generations;
            }

            static async loadVariants(generationId) {
                const cacheKey = `variants_${generationId}`;
                const storageKey = this.getCacheKey(this.STORAGE_KEYS.VARIANTS, generationId);
                
                try {
                    if (this.pendingCalls[cacheKey]) {
                     
                        return await this.pendingCalls[cacheKey];
                    }
                    
                    if (this.memoryCache[cacheKey]) {
                       
                        return this.memoryCache[cacheKey];
                    }
                    
                    const cached = this.loadFromStorage(storageKey);
                    if (cached) {
                        this.memoryCache[cacheKey] = cached;
                   
                        return cached;
                    }
                    
                   
                    this.pendingCalls[cacheKey] = this._fetchVariants(generationId, cacheKey, storageKey);
                    
                    try {
                        const result = await this.pendingCalls[cacheKey];
                        return result;
                    } finally {
                        delete this.pendingCalls[cacheKey];
                    }
                    
                } catch (error) {
                    delete this.pendingCalls[cacheKey];
                    console.error('Error loading variants:', error);
                    throw error;
                }
            }

            static async _fetchVariants(generationId, cacheKey, storageKey) {
                const response = await fetch(`${this.API_BASE}/generations/${generationId}/variants`);
                if (!response.ok) throw new Error('Failed to load variants');
                
                const data = await response.json();
                const variants = data.data || [];
                
                this.memoryCache[cacheKey] = variants;
                this.saveToStorage(storageKey, variants);
                
                
                return variants;
            }

            static async loadBodyTypes(variantId) {
                const cacheKey = `bodyTypes_${variantId}`;
                const storageKey = this.getCacheKey(this.STORAGE_KEYS.BODY_TYPES, variantId);
                
                try {
                    if (this.pendingCalls[cacheKey]) {
                      
                        return await this.pendingCalls[cacheKey];
                    }
                    
                    if (this.memoryCache[cacheKey]) {
                      
                        return this.memoryCache[cacheKey];
                    }
                    
                    const cached = this.loadFromStorage(storageKey);
                    if (cached) {
                        this.memoryCache[cacheKey] = cached;
                 
                        return cached;
                    }
                    
            
                    this.pendingCalls[cacheKey] = this._fetchBodyTypes(variantId, cacheKey, storageKey);
                    
                    try {
                        const result = await this.pendingCalls[cacheKey];
                        return result;
                    } finally {
                        delete this.pendingCalls[cacheKey];
                    }
                    
                } catch (error) {
                    delete this.pendingCalls[cacheKey];
                    console.error('Error loading body types:', error);
                    throw error;
                }
            }

            static async _fetchBodyTypes(variantId, cacheKey, storageKey) {
                const response = await fetch(`${this.API_BASE}/variants/${variantId}/body-types`);
                if (!response.ok) throw new Error('Failed to load body types');
                
                const data = await response.json();
                const bodyTypes = data.data || [];
                
                this.memoryCache[cacheKey] = bodyTypes;
                this.saveToStorage(storageKey, bodyTypes);
                
            
                return bodyTypes;
            }

            static async lookupPlate(plateNumber) {
                const cacheKey = `plate_${plateNumber}`;
                const storageKey = this.getCacheKey(this.STORAGE_KEYS.PLATE_RESULTS, plateNumber);
                
                try {
                    if (this.pendingCalls[cacheKey]) {
                        
                        return await this.pendingCalls[cacheKey];
                    }
                    
                    if (this.memoryCache[cacheKey]) {
                      
                        return this.memoryCache[cacheKey];
                    }
                    
                    const cached = this.loadFromStorage(storageKey);
                    if (cached) {
                        this.memoryCache[cacheKey] = cached;
                    
                        return cached;
                    }
                    
                
                    this.pendingCalls[cacheKey] = this._fetchPlateData(plateNumber, cacheKey, storageKey);
                    
                    try {
                        const result = await this.pendingCalls[cacheKey];
                        return result;
                    } finally {
                        delete this.pendingCalls[cacheKey];
                    }
                    
                } catch (error) {
                    delete this.pendingCalls[cacheKey];
                    console.error('Plate lookup error:', error);
                    throw error;
                }
            }

            static async _fetchPlateData(plateNumber, cacheKey, storageKey) {
                const response = await fetch(`${this.PLATE_API_BASE}/${plateNumber}/catalog`);
                if (!response.ok) throw new Error('Plate lookup failed');
                
                const result = await response.json();
                
                if (result.success && result.catalog) {
                    this.memoryCache[cacheKey] = result;
                    this.saveToStorage(storageKey, result, this.CACHE_EXPIRY.PLATE_RESULTS);
                    
                  
                    return result;
                } else {
                    throw new Error('No matching vehicle found in catalog');
                }
            }

            // NEW BUNDLE ENABLED HELPER FUNCTIONS
            static getBundleEnabledStatus(selectionData) {
                if (!selectionData) return null;
                
                return {
                    make: selectionData.makeBundleEnabled || false,
                    model: selectionData.modelBundleEnabled || false,
                    generation: selectionData.generationBundleEnabled || false,
                    variant: selectionData.variantBundleEnabled || false,
                    bodyType: selectionData.bodyTypeBundleEnabled || false
                };
            }


            static isBundleEnabledAtCurrentLevel(level, selectionData) {
                if (!selectionData) return false;
                
                const bundleStatus = this.getBundleEnabledStatus(selectionData);
                return bundleStatus[level] === true;
            }
        }

        // ============================
        // SYNC MANAGER FOR FORM MIRRORING (UPDATED)
        // ============================
        class FormSyncManager {
            static instances = [];
            static isUpdating = false;
            static isRestoring = false;
    static restoreTimeout = null;
            
            static registerInstance(instance) {
                this.instances.push(instance);
                
            }
            
            static syncSelection(sourceInstance, level, value, data = null) {
                if (this.isUpdating || this.isRestoring) return;
                
                this.isUpdating = true;
                
             
                this.instances.forEach(instance => {
                    if (instance !== sourceInstance) {
                        instance.mirrorSelection(level, value, data);
                    }
                });
                
                this.isUpdating = false;
                let globalBundle = localStorage.getItem('global_bundle');
                let elements = document.querySelectorAll('.body-kit-builder, .body-kit-builder-block ,.body-kit-builder-');

                if (globalBundle !== null) {
                    if (globalBundle === 'true') {
                        elements.forEach(el => {
                            el.style.display = '';
                            el.classList.remove('hide-body-kit');
                            el.classList.add('show-body-kit');
                        });
                    } else {
                        elements.forEach(el => {
                            el.style.display = 'none';
                            el.classList.add('hide-body-kit');
                            el.classList.remove('show-body-kit');
                        });
                    }
                }else{
                   elements.forEach(el => {
                            el.style.display = '';
                            el.classList.add('show-body-kit');
                            el.classList.remove('hide-body-kit');
                        });  
                }
                let productComplete = document.querySelector('.product-compelete');
let productCompletes = document.querySelectorAll('.product-compelete');
let fitCars = document.querySelectorAll('.fit-car');

if (productCompletes.length > 0) {
    productCompletes.forEach(el => el.style.display = 'flex');
}

if (fitCars.length > 0) {
    fitCars.forEach(el => el.style.display = 'none');
}


            }
            
            static syncPlateResult(sourceInstance, plateResult) {
                if (this.isUpdating || this.isRestoring) return;
                
                this.isUpdating = true;
                
               
                this.instances.forEach(instance => {
                    if (instance !== sourceInstance) {
                        instance.mirrorPlateResult(plateResult);
                    }
                });
                  let globalBundle = localStorage.getItem('global_bundle');
                let elements = document.querySelectorAll('.body-kit-builder, .body-kit-builder-block');

                if (globalBundle !== null) {
                    if (globalBundle === 'true') {
                        elements.forEach(el => {
                            el.style.display = '';
                            el.classList.remove('hide-body-kit');
                            el.classList.add('show-body-kit');
                        });
                    } else {
                        elements.forEach(el => {
                            el.style.display = 'none';
                            el.classList.add('hide-body-kit');
                            el.classList.remove('show-body-kit');
                        });
                    }
                }else{
                   elements.forEach(el => {
                            el.style.display = '';
                            el.classList.remove('hide-body-kit');
                            el.classList.add('show-body-kit');
                        });  
                }
                let productComplete = document.querySelector('.product-compelete');
let productCompletes = document.querySelectorAll('.product-compelete');
let fitCars = document.querySelectorAll('.fit-car');

if (productCompletes.length > 0) {
    productCompletes.forEach(el => el.style.display = 'flex');
}

if (fitCars.length > 0) {
    fitCars.forEach(el => el.style.display = 'none');
}


                this.isUpdating = false;
            }
            
            static syncReset(sourceInstance) {
                if (this.isUpdating || this.isRestoring) return;
                
                this.isUpdating = true;
                
               
                this.instances.forEach(instance => {
                    if (instance !== sourceInstance) {
                        instance.mirrorReset();
                    }
                });
           
                let globalBundle = localStorage.getItem('global_bundle');
                let elements = document.querySelectorAll('.body-kit-builder, .body-kit-builder-block');

                if (globalBundle !== null) {
                    if (globalBundle === 'true') {
                        elements.forEach(el => {
                            el.style.display = '';
                            el.classList.remove('hide-body-kit');
                            el.classList.add('show-body-kit');
                        });
                    } else {
                        elements.forEach(el => {
                            el.style.display = 'none';
                            el.classList.add('hide-body-kit');
                            el.classList.remove('show-body-kit');
                        });
                    }
                }else{
                   elements.forEach(el => {
                            el.style.display = '';
                            el.classList.remove('hide-body-kit');
                            el.classList.add('show-body-kit');
                        });  
                }
            let productCompletes = document.querySelectorAll('.product-compelete');
            let fitCars = document.querySelectorAll('.fit-car');

            if (productCompletes.length > 0) {
                productCompletes.forEach(el => el.style.display = 'flex');
            }

            if (fitCars.length > 0) {
                fitCars.forEach(el => el.style.display = 'none');
            }

                this.isUpdating = false;
            }

            static async restoreToAllForms() {
                if (this.isRestoring) return;
                
                this.isRestoring = true;
                
              
                const savedData = VehicleCacheManager.loadSavedData();
                
                if (savedData) {
                  
                    
                    for (const instance of this.instances) {
                        try {
                            await instance.restoreFromConsolidatedData(savedData);
                        } catch (error) {
                            console.error(`Failed to restore to ${instance.formId}:`, error);
                        }
                    }
                }

                let globalBundle = localStorage.getItem('global_bundle');
                let elements = document.querySelectorAll('.body-kit-builder, .body-kit-builder-block');

                if (globalBundle !== null) {
                    if (globalBundle === 'true') {
                        elements.forEach(el => {
                            el.style.display = '';
                            el.classList.remove('hide-body-kit');
                            el.classList.add('show-body-kit');
                        });
                    } else {
                        elements.forEach(el => {
                            el.style.display = 'none';
                            el.classList.add('hide-body-kit');
                             el.classList.remove('show-body-kit');
                        });
                    }
                }

                this.isRestoring = false;
            }

            // Debounced restore to avoid flicker from multiple init calls
            static scheduleRestore(delay = 150) {
                if (this.restoreTimeout) {
                    clearTimeout(this.restoreTimeout);
                }
                this.restoreTimeout = setTimeout(() => {
                    this.restoreTimeout = null;
                    this.restoreToAllForms();
                }, delay);
            }
        }

     
      // ============================
// VEHICLE SELECTOR CLASS 
// ============================
class VehicleSelector {
    constructor(containerElement) {
        this.container = containerElement;
        
        this.formId = containerElement.getAttribute('data-form-id') || 'unknown';
        if (this.formId === 'unknown') return;
      
        this.initElements();
        
        // Initialize state
        this.currentSelection = {
            makeId: null,
            modelId: null,
            generationId: null,
            variantId: null,
            bodyTypeId: null,
            makeUrl: null,
            modelUrl: null,
            generationUrl: null,
            variantUrl: null,
            bodyTypeUrl: null
        };
        
        // Track names for saving
        this.currentNames = {
            makeName: null,
            modelName: null,
            generationName: null,
            variantName: null,
            bodyTypeName: null
        };

        // NEW: Bundle enabled tracking
        this.bundleEnabled = {
            makeBundleEnabled: false,
            modelBundleEnabled: false,
            generationBundleEnabled: false,
            variantBundleEnabled: false,
            bodyTypeBundleEnabled: false
        };

        this.isRestoringUi = false;
        this.isFitmentCheck = false; // Flag to prevent saving when using fitment checker dropdown
        
        // Data storage for URL lookups
        this.makesData = [];
        this.modelsData = [];
        this.generationsData = [];
        this.variantsData = [];
        this.bodyTypesData = [];
        
        // Register with sync manager
        FormSyncManager.registerInstance(this);
        
        // Bind events
        this.bindEvents();
        
        // Initialize
        this.initialize();
      
      this.searchIntentChange();
   
    }


async checkSelectionProgress() {
   
    const makeSelected = this.makeSelect.selectedIndex > 0;
    const modelSelected = this.modelSelect.selectedIndex > 0;
    const versionSelected = this.versionSelect.selectedIndex > 0;
    const hasVariantOptions = this.variantSelect.options.length > 1;
    const variantSelected = this.variantSelect.selectedIndex > 0;
    const hasBodyTypeOptions = this.bodyTypeSelect.options.length > 1;
    const bodyTypeSelected = this.bodyTypeSelect.selectedIndex > 0;
    const hasPlateValue = this.plateInput ? !!this.plateInput.value : false;
    
    // Define completion state - all required fields must be selected
    const isFullyComplete = makeSelected && 
                           modelSelected && 
                           versionSelected && 
                           (!hasVariantOptions || variantSelected) &&
                           (!hasBodyTypeOptions || bodyTypeSelected);

         window.addEventListener('load', function () {
            if (hasPlateValue && isFullyComplete) {
                let formdetails = document.querySelectorAll('.menu-reg-form-detail');

                if (formdetails && formdetails.length > 0) {
                    setTimeout(() => {
                       formdetails.forEach(fd => fd.classList.remove('active'));
                     
                    }, 0);
                }
            }
        });


            let globalBundle = localStorage.getItem('global_bundle');
            
            if(isFullyComplete === true && globalBundle !== null && globalBundle == 'true'){
                
               let firstLink = document.querySelector('.shop-by-car #results .related-links a');
                if (firstLink) {
                    let feturl = firstLink.getAttribute('href');
                    feturl += '?view=bundle';
                     let bodyKitBuilder = document.querySelectorAll('.body-kit-builder, .body-kit-builder-block, .body-kit-builder-');
                            let bodyKitBuilderUrl = feturl; 
                            
                            if (bodyKitBuilder.length > 0) {
                                bodyKitBuilder.forEach(function(block) {
                                     if (block.hasAttribute('href')) {
                                        block.setAttribute('href', bodyKitBuilderUrl);
                                    }
                                    let links = block.querySelectorAll('[href]');
                                    //console.debug(links)
                                    links.forEach(function(link) {
                                        link.setAttribute('href', bodyKitBuilderUrl);
                                    });
                                });
                            }
                }   
            }

                if (document.body.classList.contains('bundle') && isFullyComplete === true && globalBundle !== null && globalBundle == 'true') {
                 

            if (bundleDebounceTimeout) {
                clearTimeout(bundleDebounceTimeout);
            }
            
            bundleDebounceTimeout = setTimeout(async () => {
                
                let firstLink = document.querySelector('#bundlemakeform #results .related-links a');
                if (firstLink) {
                    let feturl = firstLink.getAttribute('href');
                    
                    console.debug(globalBundle)
                    let firstLinkPath = new URL(feturl, window.location.origin).pathname;
                    let currentPath = window.location.pathname;                    
                    let firstLinkLastSegment = firstLinkPath.split('/').filter(Boolean).pop();
                    let currentLastSegment = currentPath.split('/').filter(Boolean).pop();                    
                    if (firstLinkLastSegment !== currentLastSegment) {
                        feturl += '?view=bundle';
                        const response = await fetch(feturl);
                        const data = await response.text();
                        const parser = new DOMParser();
                        const newDocument = parser.parseFromString(data, 'text/html');
                        document.querySelector('#bundle-list')
                            ?.replaceWith(newDocument.querySelector('#bundle-list'));
                         window.history.pushState({}, '', feturl); 
                            let bundleFirststep = document.querySelector('.bundle-first-step');
                    if (bundleFirststep) {
                    bundleFirststep.classList.add('active');
                    }
                 let bundleSecondStep = document.querySelector('.bundle-second-step');
                    if (bundleSecondStep) {
                    bundleSecondStep.setAttribute('open', '');
                    bundleSecondStep.classList.add('active');
                    } 
                     let bundleContainer = document.querySelector('.bundle-container');

                        if (bundleContainer) {
                            let productGroups = bundleContainer.querySelectorAll('.product-type-group');

                            if (productGroups.length > 0) {
                                let visibleCount = 0;

                                productGroups.forEach(group => {
                                    let ul = group.querySelector('.product-type-items ul');
                                    if (ul && ul.children.length === 0) {
                                        group.style.display = 'none';
                                    } else {
                                        visibleCount++;
                                    }
                                });

                                if (visibleCount === 0) {
                                    let msg = document.createElement('p');
                                    msg.className = 'no-kit-message';
                                    msg.textContent = 'Currently kit is not available';
                                    bundleContainer.appendChild(msg);
                                }
                            }
                        }

 
                    } else {
                           let bundleFirststep = document.querySelector('.bundle-first-step');
                    if (bundleFirststep) {
                    bundleFirststep.classList.add('active');
                    }
                 let bundleSecondStep = document.querySelector('.bundle-second-step');
                    if (bundleSecondStep) {
                    bundleSecondStep.setAttribute('open', '');
                    bundleSecondStep.classList.add('active');
                    }
                let bundleContainer = document.querySelector('.bundle-container');

                if (bundleContainer) {
                    let productGroups = bundleContainer.querySelectorAll('.product-type-group');

                    if (productGroups.length > 0) {
                        let visibleCount = 0;

                        productGroups.forEach(group => {
                            let ul = group.querySelector('.product-type-items ul');
                            if (ul && ul.children.length === 0) {
                                group.style.display = 'none';
                            } else {
                                visibleCount++;
                            }
                        });

                        if (visibleCount === 0) {
                            let msg = document.createElement('p');
                            msg.className = 'no-kit-message';
                            msg.textContent = 'Currently kit is not available';
                            bundleContainer.appendChild(msg);
                        }
                    }
                }


                    }
                }
            }, 500);
        }else{
            let bundleFirststep = document.querySelector('.bundle-first-step');
            if (bundleFirststep) {
            bundleFirststep.classList.remove('active');
            }

            let bundleSecondStep = document.querySelector('.bundle-second-step');
            if (bundleSecondStep) {
            bundleSecondStep.removeAttribute('open');
            bundleSecondStep.classList.remove('active');
            //localStorage.setItem('ecomify-bundle-v2', JSON.stringify([]));
              // initBundle()
            }

        }

    
    
    let urlMatched = false;
    

    if (hasPlateValue) {
        let currentUrl = window.location.href;
        let relatedLinks = this.container.querySelector('.related-links');
        
        if (relatedLinks) {
            let linkItems = relatedLinks.querySelectorAll('.link-item');
            
            // Extract just the path from current URL (ignore domain for comparison)
            // This handles preview URLs, .com, .co.uk, myshopify.com etc
            let currentPath = '';
            try {
                const urlObj = new URL(currentUrl);
                // Get path up to /products/ or the full path if no /products/
                let pathPart = urlObj.pathname.split('/products/')[0];
                currentPath = pathPart.trim().replace(/\/$/, "").toLowerCase();
            } catch (e) {
                // Fallback for older browsers
                let urlWithoutParams = currentUrl.split('?')[0];
                let currentBase = urlWithoutParams.split('/products/')[0];
                currentPath = currentBase.replace(/^https?:\/\/[^\/]+/, '').trim().replace(/\/$/, "").toLowerCase();
            }
            
            linkItems.forEach(link => {
                let linkHref = link.getAttribute('href');
                if (linkHref) {
                    // Extract just the path from link URL (ignore domain)
                    let linkPath = '';
                    try {
                        const linkUrl = new URL(linkHref, window.location.origin);
                        linkPath = linkUrl.pathname.trim().replace(/\/$/, "").toLowerCase();
                    } catch (e) {
                        // Fallback
                        let linkWithoutParams = linkHref.split('?')[0];
                        linkPath = linkWithoutParams.replace(/^https?:\/\/[^\/]+/, '').trim().replace(/\/$/, "").toLowerCase();
                    }
                    
                    if (linkPath === currentPath) {
                        urlMatched = true;
                        return;
                    }
                }
            });
        }
        
      
    }
    
 
    if (hasPlateValue) {
        const updateSelectStyle = (selectElement, isSelected) => {
            if (!isSelected) {
                selectElement.style.border = '1px solid #b00020';
            } else {
                selectElement.style.border = '';
            }
        };

        updateSelectStyle(this.makeSelect, makeSelected);
        updateSelectStyle(this.modelSelect, modelSelected);
        updateSelectStyle(this.versionSelect, versionSelected);
        updateSelectStyle(this.variantSelect, variantSelected || !hasVariantOptions);
        updateSelectStyle(this.bodyTypeSelect, bodyTypeSelected || !hasBodyTypeOptions);
    } else {
    const updateSelectStyle = (selectElement, isSelected) => {
            if (!isSelected) {
                selectElement.style.border = '1px solid #b00020';
            } else {
                selectElement.style.border = '';
            }
        };

        updateSelectStyle(this.makeSelect, makeSelected);
        updateSelectStyle(this.modelSelect, modelSelected);
        updateSelectStyle(this.versionSelect, versionSelected);
        updateSelectStyle(this.variantSelect, variantSelected || !hasVariantOptions);
        updateSelectStyle(this.bodyTypeSelect, bodyTypeSelected || !hasBodyTypeOptions);

    }
    
   
    const progress = {
        make: makeSelected ? 'selected' : 'empty',
        model: modelSelected ? 'selected' : 'empty', 
        generation: versionSelected ? 'selected' : 'empty',
        variant: variantSelected ? 'selected' : 'empty',
        bodyType: bodyTypeSelected ? 'selected' : 'empty',
        isComplete: isFullyComplete
    };
    
    
   
    if (hasPlateValue && this.container.querySelector('.menu-reg-form-detail') && makeSelected) {
        let els = this.container.querySelectorAll('.menu-reg-form-detail');
        els.forEach(el => {
            el.classList.add('active');
        });
    }
 
    let plateStatus = this.container.querySelector('.plate-status');
    let warningEl = plateStatus ? plateStatus.querySelector('.fields-warning') : null;

    if (hasPlateValue && !isFullyComplete) {
        // Selections are incomplete - show warning
        if (!warningEl && plateStatus) {
            warningEl = document.createElement('div');
            warningEl.className = 'fields-warning';
            warningEl.innerHTML = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#b00020" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path d="M12 8V12" stroke="#b00020" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path d="M12 16H12.01" stroke="#b00020" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
                <span>Fill all the fields to get the compatible parts</span>
            `;
            plateStatus.appendChild(warningEl);
        }
    } else {
        // Selections are complete or no plate - remove warning
        if (warningEl) {
            warningEl.remove();
        }
    }
    

    const bannerElements = document.querySelectorAll('.banner_result');
    if (bannerElements.length > 0 && hasPlateValue) {
        const selectedNames = [];
        
        if (makeSelected) {
            selectedNames.push(this.makeSelect.options[this.makeSelect.selectedIndex].text);
        }
        if (modelSelected) {
            selectedNames.push(this.modelSelect.options[this.modelSelect.selectedIndex].text);
        }
        if (versionSelected) {
            selectedNames.push(this.versionSelect.options[this.versionSelect.selectedIndex].text);
        }
        if (variantSelected) {
            selectedNames.push(this.variantSelect.options[this.variantSelect.selectedIndex].text);
        }
        if (bodyTypeSelected) {
            selectedNames.push(this.bodyTypeSelect.options[this.bodyTypeSelect.selectedIndex].text);
        }
        
        const resultText = selectedNames.join(' ');
        let processedText = resultText;
        const yearPattern = /\s*\((\d{4}(?:-\d{4})?)\)/g;
        const years = [];
        let match;

        while ((match = yearPattern.exec(resultText)) !== null) {
            years.push(match[1]);
        }

        processedText = resultText.replace(yearPattern, '');

        if (years.length > 0) {
            processedText = processedText.trim() + ' - (' + years[years.length - 1] + ')';
        }

        bannerElements.forEach(element => {
            element.textContent = processedText;
        });
    }
    
   
    // Consider complete if: generation selected AND (no variants OR variant selected) AND (no body types OR body type selected)
    const minimalComplete = versionSelected && 
                           (!hasVariantOptions || variantSelected) &&
                           (!hasBodyTypeOptions || bodyTypeSelected);
    
    if (hasPlateValue && minimalComplete && urlMatched) {
        let productComplete = document.querySelectorAll('.product-compelete');
        if (productComplete.length) {
            productComplete.forEach(el => el.style.display = 'none');
        }
        
        let fitCar = document.querySelectorAll('.fit-car');
        if (fitCar.length) {
            fitCar.forEach(el => el.style.display = 'flex');
        }
    }
    
 
    if (hasPlateValue && makeSelected && modelSelected) {
        // Minimal complete check for fitment warnings
       let fitmentwarning = document.querySelectorAll('.fitment-warning');
if (fitmentwarning.length > 0) {
  fitmentwarning.forEach(el => {
    el.classList.add('show');
  });
        }

        const fitmetWrappers = document.querySelectorAll('.fitmet-wrapper-missing');

        fitmetWrappers.forEach((fitmetWrapper) => {
            if (fitmetWrapper) {
                fitmetWrapper.innerHTML = '';
                
                // Show generation dropdown if not selected and has multiple options
                if (!versionSelected && this.versionSelect.options.length > 1) {
                    const generationClone = this.versionSelect.cloneNode(true);
                    generationClone.selectedIndex = this.versionSelect.selectedIndex;
                    fitmetWrapper.appendChild(generationClone);
                    
                    generationClone.addEventListener('change', (e) => {
                        // Mark as fitment check so we don't save to storage
                        this.isFitmentCheck = true;
                        this.versionSelect.selectedIndex = e.target.selectedIndex;
                        this.versionSelect.dispatchEvent(new Event('change'));
                        this.isFitmentCheck = false;
                    });
                    
                    this.versionSelect.addEventListener('change', () => {
                        generationClone.selectedIndex = this.versionSelect.selectedIndex;
                    });
                    
                } else if (versionSelected && hasVariantOptions  && !hasBodyTypeOptions) {
                    // Show variant dropdown if generation selected and variants exist
                    const variantClone = this.variantSelect.cloneNode(true);
                    variantClone.selectedIndex = this.variantSelect.selectedIndex;
                    fitmetWrapper.appendChild(variantClone);
                    
                    variantClone.addEventListener('change', (e) => {
                        // Mark as fitment check so we don't save to storage
                        this.isFitmentCheck = true;
                        this.variantSelect.selectedIndex = e.target.selectedIndex;
                        this.variantSelect.dispatchEvent(new Event('change'));
                        this.isFitmentCheck = false;
                    });
                    
                    this.variantSelect.addEventListener('change', () => {
                        variantClone.selectedIndex = this.variantSelect.selectedIndex;
                    });
                } else if (versionSelected && hasBodyTypeOptions) {
                    // Show body type dropdown if generation selected and body types exist
                    const bodyTypeClone = this.bodyTypeSelect.cloneNode(true);
                    bodyTypeClone.selectedIndex = this.bodyTypeSelect.selectedIndex;
                    fitmetWrapper.appendChild(bodyTypeClone);
                    
                    bodyTypeClone.addEventListener('change', (e) => {
                        // Mark as fitment check so we don't save to storage
                        this.isFitmentCheck = true;
                        this.bodyTypeSelect.selectedIndex = e.target.selectedIndex;
                        this.bodyTypeSelect.dispatchEvent(new Event('change'));
                        this.isFitmentCheck = false;
                    });
                    
                    this.bodyTypeSelect.addEventListener('change', () => {
                        bodyTypeClone.selectedIndex = this.bodyTypeSelect.selectedIndex;
                    });
                }
            }
        });
        
                        // Handle fitment icon visibility (outside the wrapper loop)
        const fitmentWarnings = document.querySelectorAll('.fitment-icon-wrapper');
        if (fitmentWarnings) {
            fitmentWarnings.forEach((fitmentWarning) => {
                // Minimal complete check for fitment icon
                const minimalComplete = versionSelected && 
                                       (!hasVariantOptions || variantSelected) &&
                                       (!hasBodyTypeOptions || bodyTypeSelected);
                
                // Hide icon only when URL matched AND minimal completion reached
                if (urlMatched && minimalComplete) {
                    fitmentWarning.style.display = 'none';
                } else {
                    fitmentWarning.style.display = 'flex';
                }
            });
        }
    }
    
   


   let productPage = document.querySelector('.page-type-product');

if (productPage) {

    let warningBlocks = document.querySelectorAll('.wrning-block');
    let errorBlocks = document.querySelectorAll('.s-error-block');
    let fitCarButtons = document.querySelectorAll('.fit-car');
    
    if (this.plateInput.value) {
        // Always show the warning block so the dropdown stays accessible
        warningBlocks.forEach(el => el.style.display = 'flex');
        
        if (urlMatched) {
            // Product fits - show "Fits Your Car" button, hide error
            fitCarButtons.forEach(el => el.style.display = 'flex');
            errorBlocks.forEach(el => el.style.display = 'none');
        } else {
            // Product doesn't fit - hide "Fits Your Car" button, show error
            // But keep the dropdown visible so user can change selection
            fitCarButtons.forEach(el => el.style.display = 'none');
            errorBlocks.forEach(el => el.style.display = 'flex');
        }
    }
}

   /* if (hasPlateValue && makeSelected && document.getElementById('product-template')) {
        window.addEventListener('load', function () {
            setTimeout(() => {
                let bannerBtn = document.querySelector('.banner_reg_submit');
                if (bannerBtn) {
                    // Show button only if NOT matched OR NOT complete
                    if (!urlMatched ) {
                        bannerBtn.style.display = 'block';
                    } else {
                        bannerBtn.style.display = 'none';
                    }
                }
            }, 500);
        });
    }*/
    if (this.plateInput && this.plateInput.value && makeSelected && document.getElementById('product-template') && !urlMatched) {

      
    let bannerBtn = document.querySelector('.banner_reg_submit');
    if (bannerBtn) { 
        bannerBtn.style.display = 'none';
    }else{
       bannerBtn.style.display = 'block';  
    }
   

  }  
}



   searchIntentChange(){
      function normalizeMakeForTagCheck(makeText) {
        return makeText
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/\//g, '-')
            .replace(/\s*-\s*/g, '-');
        }

        const isCollectionPage = typeof onCollectionpage !== 'undefined' && onCollectionpage;
        if (!isCollectionPage || !Array.isArray(window.collectionTags)) {
            return;
        }

        if (this.makeSelect.selectedIndex > 0 && this.plateInput && this.plateInput.value) {
            let selectedMakeText = this.makeSelect.options[this.makeSelect.selectedIndex].text;
            let normalizedMake = normalizeMakeForTagCheck(selectedMakeText);
            let makeExistsInCollection = window.collectionTags.some(tag => tag.includes(normalizedMake));

            // Don't auto-reset selections; keep persisted data across collections.
            if (!makeExistsInCollection) {
                return;
            }
        }
   }

    initElements() {
        // Plate lookup elements (may not exist if registration is disabled)
        this.plateInput = this.container.querySelector('.plate-input');
        this.plateLookupBtn = this.container.querySelector('.btn-plate-lookup');
        this.plateStatus = this.container.querySelector('.plate-status');
        
        // Dropdown selectors (always should exist)
        this.makeSelect = this.container.querySelector('.make-select');
        this.modelSelect = this.container.querySelector('.model-select');
        this.versionSelect = this.container.querySelector('.version-select');
        this.variantSelect = this.container.querySelector('.variant-select');
        this.bodyTypeSelect = this.container.querySelector('.body-type-select');
        this.bodyTypeGroup = this.container.querySelector('.body-type-group');
        
        this.resetBtn = this.container.querySelector('.btn-reset');
        this.resultsDiv = this.container.querySelector('.results');
        this.vehicleInfo = this.container.querySelector('.vehicle-info');
        this.loadingDiv = this.container.querySelector('.loading');
        this.errorDiv = this.container.querySelector('.error');
    }

    bindEvents() {
        // Plate lookup events (only if plate elements exist)
        if (this.plateLookupBtn && this.plateInput) {
            this.plateLookupBtn.addEventListener('click', () => this.handlePlateLookup());
            this.plateInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') this.handlePlateLookup();
            });
            this.plateInput.addEventListener('input', (e) => {
                // Skip during restore to prevent interference
                if (FormSyncManager.isRestoring || this.isRestoringUi) return;
                
                let value = e.target.value.trim();

                if (value === '') {
                
                const resetBtn = this.container.querySelector('.btn-reset');
                if (resetBtn) {
                    resetBtn.click();
                    
                }
                if (document.body.classList.contains('bundle')){
                
                    localStorage.setItem('ecomify-bundle-v2', JSON.stringify([]));
                    initBundle()
                    let bundleFirststep = document.querySelector('.bundle-first-step');
                    if (bundleFirststep) {
                    bundleFirststep.classList.remove('active');
                    }

                    let bundleSecondStep = document.querySelector('.bundle-second-step');
                    if (bundleSecondStep) {
                    bundleSecondStep.removeAttribute('open');
                    bundleSecondStep.classList.remove('active');
                    //localStorage.setItem('ecomify-bundle-v2', JSON.stringify([]));
                    // initBundle()
                    }
                    }
                }
            });
        }
        
        // Dropdown change events with persistence
        /*this.makeSelect.addEventListener('change', (e) => {
            
            this.handleMakeChange(e.target.value);
            const selectedMake = this.makesData.find(make => make.id == e.target.value);
            FormSyncManager.syncSelection(this, 'make', e.target.value, selectedMake);
            this.saveCurrentSelection();
        });*/

        this.makeSelect.addEventListener('change', (e) => {
        // Skip during restore to prevent interference
        if (FormSyncManager.isRestoring || this.isRestoringUi) return;
        
        const selectedMakeId = e.target.value;
        
        // Reset dependent dropdowns (don't click reset button - it clears saved state)
        this.resetDependentDropdowns(['model', 'version', 'variant', 'bodyType']);
        
        this.handleMakeChange(selectedMakeId);
        const selectedMake = this.makesData.find(make => make.id == selectedMakeId);
        FormSyncManager.syncSelection(this, 'make', selectedMakeId, selectedMake);
        this.saveCurrentSelection();
    });
        
        this.modelSelect.addEventListener('change', (e) => {
            // Skip during restore to prevent interference
            if (FormSyncManager.isRestoring || this.isRestoringUi) return;
            
            this.handleModelChange(e.target.value);
            const selectedModel = this.modelsData.find(model => model.id == e.target.value);
            FormSyncManager.syncSelection(this, 'model', e.target.value, selectedModel);
            this.currentSelection.generationId = null;
            this.currentSelection.variantId = null;
            this.currentSelection.bodyTypeId = null;
            this.saveCurrentSelection();
            if (document.body.classList.contains('bundle')){           
            localStorage.setItem('ecomify-bundle-v2', JSON.stringify([]));
              initBundle();             
            
            }
        });
        
        this.versionSelect.addEventListener('change', (e) => {
            // Skip during restore to prevent interference
            if (FormSyncManager.isRestoring || this.isRestoringUi) return;
            
            this.handleVersionChange(e.target.value);
            const selectedGeneration = this.generationsData.find(gen => gen.id == e.target.value);
            FormSyncManager.syncSelection(this, 'version', e.target.value, selectedGeneration);
            this.currentSelection.variantId = null;
            this.currentSelection.bodyTypeId = null;
            this.saveCurrentSelection();
             if (document.body.classList.contains('bundle')){
            let globalBundle = localStorage.getItem('global_bundle');            
            if(globalBundle ){
            localStorage.setItem('ecomify-bundle-v2', JSON.stringify([]));
              initBundle()
            }   
            }
        });
        
        this.variantSelect.addEventListener('change', (e) => {
            // Skip during restore to prevent interference
            if (FormSyncManager.isRestoring || this.isRestoringUi) return;
            
            this.handleVariantChange(e.target.value);
            const selectedVariant = this.variantsData.find(variant => variant.id == e.target.value);
            FormSyncManager.syncSelection(this, 'variant', e.target.value, selectedVariant);
            this.currentSelection.bodyTypeId = null;
            this.saveCurrentSelection();
            if (document.body.classList.contains('bundle')){
            let globalBundle = localStorage.getItem('global_bundle');            
            if(globalBundle ){
            localStorage.setItem('ecomify-bundle-v2', JSON.stringify([]));
              initBundle()
            }   
            }
        });
        
        this.bodyTypeSelect.addEventListener('change', (e) => {
            // Skip during restore to prevent interference
            if (FormSyncManager.isRestoring || this.isRestoringUi) return;
            
            this.handleBodyTypeChange(e.target.value);
            const selectedBodyType = this.bodyTypesData.find(bt => bt.id == e.target.value);
            FormSyncManager.syncSelection(this, 'bodyType', e.target.value, selectedBodyType);
            this.saveCurrentSelection();
             if (document.body.classList.contains('bundle')){
            let globalBundle = localStorage.getItem('global_bundle');            
            if(globalBundle ){
            localStorage.setItem('ecomify-bundle-v2', JSON.stringify([]));
              initBundle()
            }   
            }
        });

        // Reset button
        this.container.addEventListener('click', (e) => {
            if (e.target.matches('.btn-reset')) {
                // Skip during restore to prevent interference
                if (FormSyncManager.isRestoring || this.isRestoringUi) return;
                this.resetForm();
            }
        });
    }

    // NEW: Enhanced save method with bundle enabled tracking
    saveCurrentSelection() {
        // Don't save when using fitment checker dropdown (just checking, not changing selection)
        if (this.isFitmentCheck) return;
        if (!this.currentSelection.makeId) return;
        
        const selectionData = {
            makeId: this.currentSelection.makeId,
            modelId: this.currentSelection.modelId,
            generationId: this.currentSelection.generationId,
            variantId: this.currentSelection.variantId,
            bodyTypeId: this.currentSelection.bodyTypeId,
            makeUrl: this.currentSelection.makeUrl,
            modelUrl: this.currentSelection.modelUrl,
            generationUrl: this.currentSelection.generationUrl,
            variantUrl: this.currentSelection.variantUrl,
            bodyTypeUrl: this.currentSelection.bodyTypeUrl,
            makeName: this.currentNames.makeName,
            modelName: this.currentNames.modelName,
            generationName: this.currentNames.generationName,
            variantName: this.currentNames.variantName,
            bodyTypeName: this.currentNames.bodyTypeName,
            // NEW: Include bundle enabled status
            makeBundleEnabled: this.bundleEnabled.makeBundleEnabled,
            modelBundleEnabled: this.bundleEnabled.modelBundleEnabled,
            generationBundleEnabled: this.bundleEnabled.generationBundleEnabled,
            variantBundleEnabled: this.bundleEnabled.variantBundleEnabled,
            bodyTypeBundleEnabled: this.bundleEnabled.bodyTypeBundleEnabled,
            source: 'manual'
        };
       
        VehicleCacheManager.saveSelection(selectionData);
    }

    // NEW: Bundle enabled helper methods
    getBundleEnabledStatus() {
        return { ...this.bundleEnabled };
    }


    isBundleEnabledAtLevel(level) {
        return VehicleCacheManager.isBundleEnabledAtCurrentLevel(level, this.bundleEnabled);
    }


   updateBundleEnabled(level, data) {
   
    
    if (data && data.hasOwnProperty('bundleEnabled')) {
        
        
        // Map level names to correct property names
        const levelMap = {
            'make': 'makeBundleEnabled',
            'model': 'modelBundleEnabled',
            'version': 'generationBundleEnabled',  // version maps to generation
            'generation': 'generationBundleEnabled',
            'variant': 'variantBundleEnabled',
            'bodyType': 'bodyTypeBundleEnabled'
        };
        
        if (level == 'generation' || level == 'variant'  || level == 'bodyType') {            
        localStorage.setItem('global_bundle', data.bundleEnabled ? 'true' : 'false');
        }else if (level == 'make' || level == 'model') {
            localStorage.removeItem('global_bundle');
            
        }
        const propertyName = levelMap[level];
        if (propertyName) {
            this.bundleEnabled[propertyName] = data.bundleEnabled;          
          
            
        } else {
            console.warn(`Unknown level: ${level}`);
        }
    }
}

    async initialize() {
        try {
            await this.loadMakes();
            // Restore is handled centrally by initializeVehicleSelectors after all instances are ready
        } catch (error) {
            this.showError('Failed to initialize. Check API connection.');
            console.error(`${this.formId}: Initialization failed:`, error);
        }
    }

    // UPDATED CONSOLIDATED RESTORE METHOD
    async restoreFromConsolidatedData(savedData) {
        this.isRestoringUi = true;
        try {
          
            
            // Restore bundle enabled status
            if (savedData.makeBundleEnabled !== undefined) this.bundleEnabled.makeBundleEnabled = savedData.makeBundleEnabled;
            if (savedData.modelBundleEnabled !== undefined) this.bundleEnabled.modelBundleEnabled = savedData.modelBundleEnabled;
            if (savedData.generationBundleEnabled !== undefined) this.bundleEnabled.generationBundleEnabled = savedData.generationBundleEnabled;
            if (savedData.variantBundleEnabled !== undefined) this.bundleEnabled.variantBundleEnabled = savedData.variantBundleEnabled;
            if (savedData.bodyTypeBundleEnabled !== undefined) this.bundleEnabled.bodyTypeBundleEnabled = savedData.bodyTypeBundleEnabled;
            
            // If there's a plate number, populate the plate input (ALWAYS KEEP PLATE) - only if plate elements exist
            if (savedData.plateNumber) {
                if (this.plateInput) {
                    this.plateInput.value = savedData.plateNumber;
                }
                let carouselInput = document.querySelector('.banner_reg_input');
                if (carouselInput) {
                    carouselInput.value = savedData.plateNumber;
                }
                let warnings = document.querySelectorAll('.regstration-warning');
                if (warnings.length > 0) {
                warnings.forEach(warning => {
                   warning.style.setProperty('display', 'none', 'important');
                });
                }

                if (savedData.plateResult && savedData.plateResult.vehicle) {
                    const vehicle = savedData.plateResult.vehicle;
                    if (this.plateStatus) {
                        this.plateStatus.innerHTML = `${vehicle.make} ${vehicle.model} (${vehicle.yearOfManufacture})`;
                    }
                    let bannerResult = document.querySelector('.banner_result');
                    if (bannerResult) {  
                        bannerResult.textContent = `${vehicle.make}  ${vehicle.model} (${vehicle.yearOfManufacture})`;
                    }
                }
            }
            
            // Ensure makes are loaded
            if (this.makesData.length === 0) {
                await this.loadMakes();
            }
            
            // Restore selection hierarchy
            if (savedData.makeId) {
                const makeExists = this.makesData.find(make => make.id == savedData.makeId);
                if (makeExists) {
                    this.makeSelect.value = savedData.makeId;
                    this.currentSelection.makeId = savedData.makeId;
                    this.currentSelection.makeUrl = savedData.makeUrl;
                    this.currentNames.makeName = savedData.makeName;
                    
                    await this.loadModels(savedData.makeId, true);
                    
                    // Restore model
                    if (savedData.modelId) {
                        const modelExists = this.modelsData.find(model => model.id == savedData.modelId);
                        if (modelExists) {
                            this.modelSelect.value = savedData.modelId;
                            this.currentSelection.modelId = savedData.modelId;
                            this.currentSelection.modelUrl = savedData.modelUrl;
                            this.currentNames.modelName = savedData.modelName;
                            
                            await this.loadGenerations(savedData.modelId, true);
                            
                            // Restore generation
                            if (savedData.generationId) {
                                const genExists = this.generationsData.find(gen => gen.id == savedData.generationId);
                                if (genExists) {
                                    this.versionSelect.value = savedData.generationId;
                                    this.currentSelection.generationId = savedData.generationId;
                                    this.currentSelection.generationUrl = savedData.generationUrl;
                                    this.currentNames.generationName = savedData.generationName;
                                    
                                    await this.loadVariants(savedData.generationId, true);
                                    
                                    // Restore variant
                                    if (savedData.variantId) {
                                        const variantExists = this.variantsData.find(variant => variant.id == savedData.variantId);
                                        if (variantExists) {
                                            this.variantSelect.value = savedData.variantId;
                                            this.currentSelection.variantId = savedData.variantId;
                                            this.currentSelection.variantUrl = savedData.variantUrl;
                                            this.currentNames.variantName = savedData.variantName;
                                            
                                            await this.loadBodyTypes(savedData.variantId, true);
                                            
                                            // Restore body type
                                            if (savedData.bodyTypeId && this.bodyTypesData.length > 0) {
                                                const bodyTypeExists = this.bodyTypesData.find(bt => bt.id == savedData.bodyTypeId);
                                                if (bodyTypeExists) {
                                                    this.bodyTypeSelect.value = savedData.bodyTypeId;
                                                    this.currentSelection.bodyTypeId = savedData.bodyTypeId;
                                                    this.currentSelection.bodyTypeUrl = savedData.bodyTypeUrl;
                                                    this.currentNames.bodyTypeName = savedData.bodyTypeName;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            
            this.updateVehicleInfo();
           
        } catch (error) {
            console.error(`${this.formId}: Restore from consolidated data error:`, error);
        } finally {
            this.isRestoringUi = false;
            this.updateVehicleInfo();
        }
    }

    async handlePlateLookup() {
        // Check if plate input exists (may be disabled via settings)
        if (!this.plateInput) {
            console.warn('Plate lookup is disabled');
            return;
        }
        
        const plateNumber = this.plateInput.value.trim().toUpperCase();
        if (plateNumber.length < 4) {
            if (this.plateStatus) {
                this.plateStatus.innerHTML = 'Please enter a valid UK plate number';
            }
            return;
        }
        localStorage.removeItem('global_bundle');
        let menuForm = this.container.closest ? this.container.closest('.menu-reg-form') : this.container;
        if (!menuForm) menuForm = this.container;
        try {
            menuForm.classList.add('is-loading');
            if (this.plateStatus) this.plateStatus.innerHTML = '';
            if (this.plateLookupBtn) {
                this.plateLookupBtn.disabled = true;
                this.plateLookupBtn.setAttribute('aria-busy', 'true');
            }
            
            const result = await VehicleCacheManager.lookupPlate(plateNumber);
            if (this.plateStatus) {
                this.plateStatus.innerHTML = `${result.vehicle.make} ${result.vehicle.model} (${result.vehicle.yearOfManufacture})`;
                let warnings = document.querySelectorAll('.regstration-warning');
                if (warnings.length > 0) {
                warnings.forEach(warning => {
                   warning.style.setProperty('display', 'none', 'important');
                });
                }

            }
            
            FormSyncManager.syncPlateResult(this, result);
            await this.autoPopulateFromPlate(result, plateNumber);
            
        } catch (error) {
            if (this.plateStatus) this.plateStatus.innerHTML = 'Vehicle not found';
            console.error(`${this.formId}: Plate lookup error:`, error);
            let bannerResult = document.querySelector('.banner_result');
            if (bannerResult) bannerResult.textContent = 'Vehicle not found';
        } finally {
            menuForm.classList.remove('is-loading');
            if (this.plateLookupBtn) {
                this.plateLookupBtn.disabled = false;
                this.plateLookupBtn.removeAttribute('aria-busy');
            }
            document.dispatchEvent(new CustomEvent('registration-lookup-complete'));
        }
    }

    resetForm() {
        // Clear saved state when manually resetting (including plate)
        VehicleCacheManager.clearSavedState();
        
        FormSyncManager.syncReset(this);
        this.performReset();
    }

    performReset() {
        // Reset all dropdowns
        this.makeSelect.value = '';
        this.resetDependentDropdowns(['model', 'version', 'variant', 'bodyType']);
        
        // Reset plate lookup (only if plate elements exist)
        if (this.plateInput) {
            this.plateInput.value = '';
        }
        let carouselInput = document.querySelector('.banner_reg_input');
        if (carouselInput) {
            carouselInput.value = '';
        }
        if (this.plateStatus) {
            this.plateStatus.innerHTML = '';
        }
        let bannerResult = document.querySelector('.banner_result');
        if (bannerResult) {
            bannerResult.textContent = '';
        }
        if (this.vehicleInfo) {
            this.vehicleInfo.innerHTML = '';
        }
        
        // Reset results
        this.resultsDiv.classList.remove('show');
        
        // Reset selection state
        this.currentSelection = {
            makeId: null,
            modelId: null,
            generationId: null,
            variantId: null,
            bodyTypeId: null,
            makeUrl: null,
            modelUrl: null,
            generationUrl: null,
            variantUrl: null,
            bodyTypeUrl: null
        };
        
        this.currentNames = {
            makeName: null,
            modelName: null,
            generationName: null,
            variantName: null,
            bodyTypeName: null
        };

        // Reset bundle enabled status
        this.bundleEnabled = {
            makeBundleEnabled: false,
            modelBundleEnabled: false,
            generationBundleEnabled: false,
            variantBundleEnabled: false,
            bodyTypeBundleEnabled: false
        };
        localStorage.removeItem('global_bundle');
        let warnings = document.querySelectorAll('.regstration-warning');
                if (warnings.length > 0) {
                warnings.forEach(warning => {
                    warning.style.display = '';
                });
         }
         let fitmentwarning = document.querySelectorAll('.fitment-warning');
        if (fitmentwarning.length > 0) {
        fitmentwarning.forEach(el => {
            el.classList.remove('show');
        });
        }
          let bannerBtn = document.querySelector('.banner_reg_submit');
            if (bannerBtn) {   
            bannerBtn.style.display = 'block';  
            }
         let errorBlocks = document.querySelectorAll('.s-error-block');
            if (errorBlocks.length > 0) {
            errorBlocks.forEach(el => el.style.display = 'none');
            }
            let bodyKitBuilder = document.querySelectorAll('.body-kit-builder, .body-kit-builder-block, .body-kit-builder-');
            let bodyKitBuilderUrl = '/collections/all?view=bundle';

            if (bodyKitBuilder.length > 0) {
                bodyKitBuilder.forEach(function(block) {
                     if (block.hasAttribute('href')) {
                       block.setAttribute('href', bodyKitBuilderUrl);
                      }
                    let links = block.querySelectorAll('[href]');           
                    links.forEach(function(link) {
                        link.setAttribute('href', bodyKitBuilderUrl);
                    });
                });
            }
 
    }

    showError(message) {
        this.errorDiv.textContent = message;
        this.errorDiv.style.display = 'block';
        setTimeout(() => {
            this.errorDiv.style.display = 'none';
        }, 5000);
    }

    
updateVehicleInfo() {
    if (this.isRestoringUi) return;
    const make = this.makeSelect.options[this.makeSelect.selectedIndex]?.text || '';
    const model = this.modelSelect.options[this.modelSelect.selectedIndex]?.text || '';
    const version = this.versionSelect.options[this.versionSelect.selectedIndex]?.text || '';
    const variant = this.variantSelect.options[this.variantSelect.selectedIndex]?.text || '';
    const bodyType = this.bodyTypeSelect.options[this.bodyTypeSelect.selectedIndex]?.text || '';

    if (make && make !== 'Select Make') {
        this.resultsDiv.classList.add('show');
        
        let info = `<div class="vehicle-info-wrapper">
                        <div class="vehicle-summary">
                            <h3 class="vehicle-title">${make}`;
        if (model && model !== 'Select Model') info += ` ${model}`;
        info += `</h3>
                        </div>`;
        
        // Vehicle details section
        info += '<div class="vehicle-details">';
        
        if (version && version !== 'Select Generation') {
            info += `<div class="detail-item">
                        <span class="detail-label">Generation:</span> 
                        <span class="detail-value">${version}</span>
                    </div>`;
        }
        if (variant && variant !== 'Select Variant') {
            info += `<div class="detail-item">
                        <span class="detail-label">Variant:</span> 
                        <span class="detail-value">${variant}</span>
                    </div>`;
        }
        if (bodyType && bodyType !== 'Select Body Type') {
            info += `<div class="detail-item">
                        <span class="detail-label">Body Type:</span> 
                        <span class="detail-value">${bodyType}</span>
                    </div>`;
        }
        
        info += '</div>'; // Close vehicle-details
        
        // URLs section in the order: Body Type → Variant → Generation → Model → Make
        const urlsToShow = [];
        
        if (this.currentSelection.bodyTypeUrl && bodyType && bodyType !== 'Select Body Type') {
            urlsToShow.push({
                label: `${bodyType} Details`,
                url: this.currentSelection.bodyTypeUrl,
                class: 'urlbodytype'
            });
        }
        
        if (this.currentSelection.variantUrl && variant && variant !== 'Select Variant') {
            urlsToShow.push({
                label: `${variant} Details`,
                url: this.currentSelection.variantUrl,
                class: 'urlvariant'
            });
        }
        
        if (this.currentSelection.generationUrl && version && version !== 'Select Generation') {
            urlsToShow.push({
                label: `${version} Details`,
                url: this.currentSelection.generationUrl,
                class: 'urlgeneration'
            });
        }
        
        if (this.currentSelection.modelUrl && model && model !== 'Select Model') {
            const modelText = model !== 'Select Model' ? model : 'Model';
            urlsToShow.push({
                label: `${modelText} Products`,
                url: this.currentSelection.modelUrl,
                class: 'urlmodel'
            });
        }
        
        if (this.currentSelection.makeUrl) {
            urlsToShow.push({
                label: `${make} Products`,
                url: this.currentSelection.makeUrl,
                class: 'urlmake'
            });
        }
        
        // Display URLs if any exist
        /*if (urlsToShow.length > 0) {
            info += `<div class="related-links">
                        <h4 class="links-title">Related Links</h4>
                        <div class="links-grid">`;
            
            urlsToShow.forEach(link => {
                info += `<a class="link-item ${link.class}" href="${link.url}" target="_blank">
                            <span class="link-text">${link.label}</span>
                            <span class="link-arrow">→</span>
                        </a>`;
            });
            
            info += `</div>
                    </div>`;
        }*/
        if (urlsToShow.length > 0) {
            const currentHostname = window.location.hostname;
            const isPreviewDomain = currentHostname.includes('shopifypreview.com');
            const isComDomain = currentHostname.includes('.com') && !isPreviewDomain;
            const currentOrigin = window.location.origin;
            
            info += `<div class="related-links">
                        <h4 class="links-title">Related Links</h4>
                        <div class="links-grid">`;
            
            urlsToShow.forEach(link => {
                let adjustedUrl = link.url;
                
                // If on Shopify preview URL, replace any production domain with current preview domain
                if (isPreviewDomain) {
                    try {
                        const linkUrl = new URL(link.url);
                        // Keep just the path and use current preview origin
                        adjustedUrl = currentOrigin + linkUrl.pathname + linkUrl.search;
                    } catch (e) {
                        // Fallback: extract path and prepend current origin
                        const pathMatch = link.url.match(/\/collections\/[^\?#]*/);
                        if (pathMatch) {
                            adjustedUrl = currentOrigin + pathMatch[0];
                        }
                    }
                } else if (isComDomain) {
                    // On .com production site, convert .co.uk to .com
                    adjustedUrl = link.url.replace('.co.uk', '.com');
                }
                
                info += `<a class="link-item ${link.class}" href="${adjustedUrl}" target="_blank">
                            <span class="link-text">${link.label}</span>
                            <span class="link-arrow">→</span>
                        </a>`;
            });
            
            info += `</div>
                    </div>`;
        }
        info += '</div>'; // Close vehicle-info-wrapper
        if (urlsToShow.length > 0 && document.body.classList.contains('page-type-article')) {
            //window.location.href = urlsToShow[0].url;
        }

        // Add error message outside wrapper if no links
        if (urlsToShow.length === 0) {
            info += `<div class="error-message">
                        No related links available for this selection.
                    </div>`;
        }
        

        this.vehicleInfo.innerHTML = info;

       this.checkSelectionProgress();
    } else {
        this.resultsDiv.classList.remove('show');
    }
}
    // ============================
    // MIRROR METHODS FOR SYNC (UPDATED WITH BUNDLE TRACKING)
    // ============================
    async mirrorSelection(level, value, data = null) {
        try {
            switch (level) {
                case 'make':
                    if (value) {
                        if (this.makesData.length === 0) {
                            await this.loadMakes();
                        }
                        
                        this.makeSelect.value = value;
                        this.currentSelection.makeId = value;
                        this.currentSelection.makeUrl = data?.url || null;
                        this.currentNames.makeName = data?.name || null;
                        this.updateBundleEnabled('make', data);
                        
                        await this.loadModels(value, true);
                    } else {
                        this.makeSelect.value = '';
                        this.currentSelection.makeId = null;
                        this.currentSelection.makeUrl = null;
                        this.currentNames.makeName = null;
                        this.bundleEnabled.makeBundleEnabled = false;
                        this.resetDependentDropdowns(['model', 'version', 'variant', 'bodyType']);
                    }
                    break;
                    
                case 'model':
                    if (value && this.currentSelection.makeId) {
                        this.modelSelect.value = value;
                        this.currentSelection.modelId = value;
                        this.currentSelection.modelUrl = data?.url || null;
                        this.currentNames.modelName = data?.name || null;
                        this.updateBundleEnabled('model', data);
                        
                        await this.loadGenerations(value, true);
                    } else {
                        this.modelSelect.value = '';
                        this.currentSelection.modelId = null;
                        this.currentSelection.modelUrl = null;
                        this.currentNames.modelName = null;
                        this.bundleEnabled.modelBundleEnabled = false;
                        this.resetDependentDropdowns(['version', 'variant', 'bodyType']);
                    }
                    break;
                    
                case 'version':
                    if (value && this.currentSelection.modelId) {
                        this.versionSelect.value = value;
                        this.currentSelection.generationId = value;
                        this.currentSelection.generationUrl = data?.url || null;
                        this.currentNames.generationName = data?.name || null;
                        this.updateBundleEnabled('generation', data);
                        
                        await this.loadVariants(value, true);
                    } else {
                        this.versionSelect.value = '';
                        this.currentSelection.generationId = null;
                        this.currentSelection.generationUrl = null;
                        this.currentNames.generationName = null;
                        this.bundleEnabled.generationBundleEnabled = false;
                        this.resetDependentDropdowns(['variant', 'bodyType']);
                    }
                    break;
                    
                case 'variant':
                    if (value && this.currentSelection.generationId) {
                        this.variantSelect.value = value;
                        this.currentSelection.variantId = value;
                        this.currentSelection.variantUrl = data?.url || null;
                        this.currentNames.variantName = data?.name || null;
                        this.updateBundleEnabled('variant', data);
                        
                        await this.loadBodyTypes(value, true);
                    } else {
                        this.variantSelect.value = '';
                        this.currentSelection.variantId = null;
                        this.currentSelection.variantUrl = null;
                        this.currentNames.variantName = null;
                        this.bundleEnabled.variantBundleEnabled = false;
                        this.resetDependentDropdowns(['bodyType']);
                    }
                    break;
                    
                case 'bodyType':
                    if (value && this.currentSelection.variantId) {
                        this.bodyTypeSelect.value = value;
                        this.currentSelection.bodyTypeId = value;
                        this.currentSelection.bodyTypeUrl = data?.url || null;
                        this.currentNames.bodyTypeName = data?.name || null;
                        this.updateBundleEnabled('bodyType', data);
                    } else {
                        this.bodyTypeSelect.value = '';
                        this.currentSelection.bodyTypeId = null;
                        this.currentSelection.bodyTypeUrl = null;
                        this.currentNames.bodyTypeName = null;
                        this.bundleEnabled.bodyTypeBundleEnabled = false;
                    }
                    break;
            }
            
            this.updateVehicleInfo();
        } catch (error) {
            console.error(`${this.formId}: Mirror error for ${level}:`, error);
        }
    }

    async mirrorPlateResult(plateResult) {
   
        
        this.plateInput.value = plateResult.vehicle.registrationNumber;              
        
        let bannerResultInput = document.querySelector('.banner_reg_input');
        if (bannerResultInput) {
            bannerResultInput.value = plateResult.vehicle.registrationNumber;
        }
        
        if (this.plateStatus) {
            this.plateStatus.innerHTML = `${plateResult.vehicle.make} ${plateResult.vehicle.model} (${plateResult.vehicle.yearOfManufacture})`;
        }
        let bannerResult = document.querySelector('.banner_result');
        if (bannerResult) {
            bannerResult.textContent = `${plateResult.vehicle.make} ${plateResult.vehicle.model} (${plateResult.vehicle.yearOfManufacture})`;
        }
        await this.autoPopulateFromPlate(plateResult, plateResult.vehicle.registrationNumber);
    }

    mirrorReset() {

        this.performReset();
    }

    // UPDATED AUTO-POPULATE WITH BUNDLE TRACKING
    async autoPopulateFromPlate(plateResult, plateNumber, isRestore = false) {
        try {
            const { catalog } = plateResult;
            if (!isRestore && this.plateStatus) {
                this.plateStatus.innerHTML += '';
            }
            
            // Ensure makes are loaded
            if (this.makesData.length === 0) {
                await this.loadMakes();
            }
            
            // Sequentially select any levels present in catalog.matched
            const matched = catalog && catalog.matched ? catalog.matched : {};

            // Make
            if (matched.make) {
                const makeOption = Array.from(this.makeSelect.options).find(opt => opt.value == matched.make.id);
                if (makeOption) {
                    this.makeSelect.value = makeOption.value;
                    this.currentSelection.makeId = makeOption.value;
                    this.currentSelection.makeUrl = matched.make.url;
                    this.currentNames.makeName = matched.make.name;
                    this.updateBundleEnabled('make', matched.make);
                    this.updateVehicleInfo();
                    if (!FormSyncManager.isRestoring) {
                        FormSyncManager.syncSelection(this, 'make', makeOption.value, matched.make);
                    }
                    await this.loadModels(makeOption.value);
                }
            }

            // Model (requires make)
            if (matched.model && this.currentSelection.makeId) {
                const modelOption = Array.from(this.modelSelect.options).find(opt => opt.value == matched.model.id);
                if (modelOption) {
                    this.modelSelect.value = modelOption.value;
                    this.currentSelection.modelId = modelOption.value;
                    this.currentSelection.modelUrl = matched.model.url;
                    this.currentNames.modelName = matched.model.name;
                    this.updateBundleEnabled('model', matched.model);
                    this.updateVehicleInfo();
                    if (!FormSyncManager.isRestoring) {
                        FormSyncManager.syncSelection(this, 'model', modelOption.value, matched.model);
                    }
                    await this.loadGenerations(modelOption.value);
                }
            }

            // Generation (requires model)
            if (matched.generation && this.currentSelection.modelId) {
                const generationOption = Array.from(this.versionSelect.options).find(opt => opt.value == matched.generation.id);
                if (generationOption) {
                    this.versionSelect.value = generationOption.value;
                    this.currentSelection.generationId = generationOption.value;
                    this.currentSelection.generationUrl = matched.generation.url;
                    this.currentNames.generationName = matched.generation.name;
                    this.updateBundleEnabled('generation', matched.generation);
                    this.updateVehicleInfo();
                    if (!FormSyncManager.isRestoring) {
                        FormSyncManager.syncSelection(this, 'version', generationOption.value, matched.generation);
                    }
                    await this.loadVariants(generationOption.value);
                }
            }

            // Variant (requires generation)
            if (matched.variant && this.currentSelection.generationId) {
                const variantOption = Array.from(this.variantSelect.options).find(opt => opt.value == matched.variant.id);
                if (variantOption) {
                    this.variantSelect.value = variantOption.value;
                    this.currentSelection.variantId = variantOption.value;
                    this.currentSelection.variantUrl = matched.variant.url;
                    this.currentNames.variantName = matched.variant.name;
                    this.updateBundleEnabled('variant', matched.variant);
                    this.updateVehicleInfo();
                    if (!FormSyncManager.isRestoring) {
                        FormSyncManager.syncSelection(this, 'variant', variantOption.value, matched.variant);
                    }
                    await this.loadBodyTypes(variantOption.value);
                }
            }

            // Body Type (requires variant)
            if (matched.bodyType && this.currentSelection.variantId) {
                const bodyTypeOption = Array.from(this.bodyTypeSelect.options).find(opt => opt.value == matched.bodyType.id);
                if (bodyTypeOption) {
                    this.bodyTypeSelect.value = bodyTypeOption.value;
                    this.currentSelection.bodyTypeId = bodyTypeOption.value;
                    this.currentSelection.bodyTypeUrl = matched.bodyType.url;
                    this.currentNames.bodyTypeName = matched.bodyType.name;
                    this.updateBundleEnabled('bodyType', matched.bodyType);
                    this.updateVehicleInfo();
                    if (!FormSyncManager.isRestoring) {
                        FormSyncManager.syncSelection(this, 'bodyType', bodyTypeOption.value, matched.bodyType);
                    }
                }
            }
            
            // Save the consolidated data after plate auto-population (UPDATED WITH BUNDLE STATUS)
            if (!FormSyncManager.isRestoring) {
                const consolidatedData = {
                    // Selection data
                    makeId: this.currentSelection.makeId,
                    modelId: this.currentSelection.modelId,
                    generationId: this.currentSelection.generationId,
                    variantId: this.currentSelection.variantId,
                    bodyTypeId: this.currentSelection.bodyTypeId,
                    makeUrl: this.currentSelection.makeUrl,
                    modelUrl: this.currentSelection.modelUrl,
                    generationUrl: this.currentSelection.generationUrl,
                    variantUrl: this.currentSelection.variantUrl,
                    bodyTypeUrl: this.currentSelection.bodyTypeUrl,
                    makeName: this.currentNames.makeName,
                    modelName: this.currentNames.modelName,
                    generationName: this.currentNames.generationName,
                    variantName: this.currentNames.variantName,
                    bodyTypeName: this.currentNames.bodyTypeName,
                    // Bundle enabled status
                    makeBundleEnabled: this.bundleEnabled.makeBundleEnabled,
                    modelBundleEnabled: this.bundleEnabled.modelBundleEnabled,
                    generationBundleEnabled: this.bundleEnabled.generationBundleEnabled,
                    variantBundleEnabled: this.bundleEnabled.variantBundleEnabled,
                    bodyTypeBundleEnabled: this.bundleEnabled.bodyTypeBundleEnabled,
                    // Plate data
                    plateNumber: plateNumber,
                    plateResult: plateResult,
                    source: 'plate'
                };
                
                VehicleCacheManager.saveData(consolidatedData);
            }
            
        } catch (error) {
            console.error(`${this.formId}: Auto-populate error:`, error);
            if (!isRestore && this.plateStatus) {
                this.plateStatus.innerHTML += ' (auto-populate failed)';
            }
        }
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // ============================
    // LOADING AND POPULATION METHODS (UPDATED WITH BUNDLE TRACKING)
    // ============================
    async loadMakes() {
        try {
            this.makesData = await VehicleCacheManager.loadMakes();
            this.populateMakeDropdown();
        } catch (error) {
            this.showError('Failed to load makes. Is the API running?');
            throw error;
        }
    }

    populateMakeDropdown() {
        this.makeSelect.innerHTML = '<option value="">Select Make</option>';
        this.makesData.forEach(make => {
            const option = new Option(make.name, make.id);
            this.makeSelect.add(option);
        });
    }

    async handleMakeChange(makeId) {
        if (makeId) {
            await this.loadModels(makeId);
            const selectedMake = this.makesData.find(make => make.id == makeId);
            this.currentNames.makeName = selectedMake?.name || null;
            this.updateBundleEnabled('make', selectedMake);
        } else {
            this.resetDependentDropdowns(['model', 'version', 'variant', 'bodyType']);
            this.currentNames.makeName = null;
            this.bundleEnabled.makeBundleEnabled = false;
            this.updateVehicleInfo();
        }
    }

    async loadModels(makeId, silent = false) {
        try {
            if (!silent) this.showLoading();
            this.currentSelection.makeId = makeId;
            
            const selectedMake = this.makesData.find(make => make.id == makeId);
            this.currentSelection.makeUrl = selectedMake?.url || null;
            
            this.modelsData = await VehicleCacheManager.loadModels(makeId);
            this.populateModelDropdown();
            this.resetDependentDropdowns(['version', 'variant', 'bodyType']);
            this.updateVehicleInfo();
            
        } catch (error) {
            if (!silent) this.showError('Failed to load models');
            console.error(`${this.formId}: Error loading models:`, error);
        } finally {
            if (!silent) this.hideLoading();
        }
    }

    populateModelDropdown() {
        this.modelSelect.innerHTML = '<option value="">Select Model</option>';
        this.modelsData.forEach(model => {
            const option = new Option(model.name, model.id);
            this.modelSelect.add(option);
        });
        this.modelSelect.disabled = false;
    }

    async handleModelChange(modelId) {
        if (modelId) {
            await this.loadGenerations(modelId);
            const selectedModel = this.modelsData.find(model => model.id == modelId);
            this.currentNames.modelName = selectedModel?.name || null;
            this.updateBundleEnabled('model', selectedModel);
        } else {
            this.resetDependentDropdowns(['version', 'variant', 'bodyType']);
            this.currentNames.modelName = null;
            this.bundleEnabled.modelBundleEnabled = false;
            this.updateVehicleInfo();
        }
    }

    async loadGenerations(modelId, silent = false) {
        try {
            if (!silent) this.showLoading();
            this.currentSelection.modelId = modelId;
            
            const selectedModel = this.modelsData.find(model => model.id == modelId);
            this.currentSelection.modelUrl = selectedModel?.url || null;
            
            this.generationsData = await VehicleCacheManager.loadGenerations(modelId);
            this.populateVersionDropdown();
            this.resetDependentDropdowns(['variant', 'bodyType']);
            this.updateVehicleInfo();
            
        } catch (error) {
            if (!silent) this.showError('Failed to load generations');
            console.error(`${this.formId}: Error loading generations:`, error);
        } finally {
            if (!silent) this.hideLoading();
        }
    }

    populateVersionDropdown() {
        this.versionSelect.innerHTML = '<option value="">Select Generation</option>';
        this.generationsData.forEach(gen => {
            const option = new Option(gen.name, gen.id);
            this.versionSelect.add(option);
        });
        this.versionSelect.disabled = false;
    }

    async handleVersionChange(generationId) {
        if (generationId) {
            await this.loadVariants(generationId);
            const selectedGeneration = this.generationsData.find(gen => gen.id == generationId);
            this.currentNames.generationName = selectedGeneration?.name || null;
            this.updateBundleEnabled('generation', selectedGeneration);
        } else {
            this.resetDependentDropdowns(['variant', 'bodyType']);
            this.currentNames.generationName = null;
            this.bundleEnabled.generationBundleEnabled = false;
            this.updateVehicleInfo();
        }
    }

    async loadVariants(generationId, silent = false) {
        try {
            if (!silent) this.showLoading();
            this.currentSelection.generationId = generationId;
            
            const selectedGeneration = this.generationsData.find(gen => gen.id == generationId);
            this.currentSelection.generationUrl = selectedGeneration?.url || null;
            
            this.variantsData = await VehicleCacheManager.loadVariants(generationId);
            this.populateVariantDropdown();
            this.resetDependentDropdowns(['bodyType']);
            this.updateVehicleInfo();
            
        } catch (error) {
            if (!silent) this.showError('Failed to load variants');
            console.error(`${this.formId}: Error loading variants:`, error);
        } finally {
            if (!silent) this.hideLoading();
        }
    }

    populateVariantDropdown() {
         if (this.variantsData.length === 0) {
        // Hide dropdown or show "No variants available" message
        this.variantSelect.style.display = 'none';
        return;
    }this.variantSelect.style.display = 'block';
        this.variantSelect.innerHTML = '<option value="">Select Variant</option>';
        this.variantsData.forEach(variant => {
            const option = new Option(variant.name, variant.id);
            this.variantSelect.add(option);
        });
        this.variantSelect.disabled = false;
    }

    async handleVariantChange(variantId) {
        if (variantId) {
            await this.loadBodyTypes(variantId);
            const selectedVariant = this.variantsData.find(variant => variant.id == variantId);
            this.currentNames.variantName = selectedVariant?.name || null;
            this.updateBundleEnabled('variant', selectedVariant);
        } else {
            this.resetDependentDropdowns(['bodyType']);
            this.currentNames.variantName = null;
            this.bundleEnabled.variantBundleEnabled = false;
            this.updateVehicleInfo();
        }
    }

    async loadBodyTypes(variantId, silent = false) {
        try {
            if (!silent) this.showLoading();
            this.currentSelection.variantId = variantId;
            
            const selectedVariant = this.variantsData.find(variant => variant.id == variantId);
            this.currentSelection.variantUrl = selectedVariant?.url || null;
            
            this.bodyTypesData = await VehicleCacheManager.loadBodyTypes(variantId);
            
            if (this.bodyTypesData.length > 0) {
                this.populateBodyTypeDropdown();
                this.bodyTypeGroup.style.display = 'block';
            } else {
                this.bodyTypeGroup.style.display = 'none';
            }
            
            this.updateVehicleInfo();
            
        } catch (error) {
            if (!silent) this.showError('Failed to load body types');
            console.error(`${this.formId}: Error loading body types:`, error);
        } finally {
            if (!silent) this.hideLoading();
        }
    }

    populateBodyTypeDropdown() {
        this.bodyTypeSelect.innerHTML = '<option value="">Select Body Type</option>';
        this.bodyTypesData.forEach(bt => {
            const option = new Option(bt.name, bt.id);
            this.bodyTypeSelect.add(option);
        });
        this.bodyTypeSelect.disabled = false;
    }

    handleBodyTypeChange(bodyTypeId) {
        this.currentSelection.bodyTypeId = bodyTypeId;
        
        if (bodyTypeId) {
            const selectedBodyType = this.bodyTypesData.find(bt => bt.id == bodyTypeId);
            this.currentSelection.bodyTypeUrl = selectedBodyType?.url || null;
            this.currentNames.bodyTypeName = selectedBodyType?.name || null;
            this.updateBundleEnabled('bodyType', selectedBodyType);
        } else {
            this.currentSelection.bodyTypeUrl = null;
            this.currentNames.bodyTypeName = null;
            this.bundleEnabled.bodyTypeBundleEnabled = false;
        }
        
        this.updateVehicleInfo();
    }

    resetDependentDropdowns(dropdowns) {
        if (dropdowns.includes('model')) {
            this.modelSelect.innerHTML = '<option value="">Select Model</option>';
            this.modelSelect.disabled = true;
            this.currentNames.modelName = null;
            this.bundleEnabled.modelBundleEnabled = false;
        }
        if (dropdowns.includes('version')) {
            this.versionSelect.innerHTML = '<option value="">Select Generation</option>';
            this.versionSelect.disabled = true;
            this.currentNames.generationName = null;
            this.bundleEnabled.generationBundleEnabled = false;
        }
        if (dropdowns.includes('variant')) {
            this.variantSelect.innerHTML = '<option value="">Select Variant</option>';
            this.variantSelect.disabled = true;
            this.currentNames.variantName = null;
            this.bundleEnabled.variantBundleEnabled = false;
        }
        if (dropdowns.includes('bodyType')) {
            this.bodyTypeSelect.innerHTML = '<option value="">Select Body Type</option>';
            this.bodyTypeSelect.disabled = true;
            this.bodyTypeGroup.style.display = 'none';
            this.currentNames.bodyTypeName = null;
            this.bundleEnabled.bodyTypeBundleEnabled = false;
        }
    }

    showLoading() {
        var menuForm = this.container.closest ? this.container.closest('.menu-reg-form') : this.container;
        if (!menuForm) menuForm = this.container;
        menuForm.classList.add('is-loading');
        if (this.loadingDiv) this.loadingDiv.style.display = 'block';
    }

    hideLoading() {
        var menuForm = this.container.closest ? this.container.closest('.menu-reg-form') : this.container;
        if (!menuForm) menuForm = this.container;
        menuForm.classList.remove('is-loading');
        if (this.loadingDiv) this.loadingDiv.style.display = 'none';
    }
}

  // ============================
        // GLOBAL INITIALIZATION (UPDATED)
        // ============================
        document.addEventListener('DOMContentLoaded', function() {
            // Check if registration elements exist (might be disabled via theme settings)
            const containers = document.querySelectorAll('.menu-reg-form');
            
            if (containers.length === 0) {
                console.log('Vehicle registration is disabled or no registration forms found.');
                return;
            }
            
            // Initialize storage info
            VehicleCacheManager.updateGlobalStorageInfo();
            
            // Initialize all vehicle selectors (avoid duplicate instances)
            const selectors = initializeVehicleSelectors();
            
           
            
            // Global storage controls (updated for consolidated storage)
            const globalShowStorage = document.getElementById('global-show-storage');
            const globalClearStorage = document.getElementById('global-clear-storage');
            const globalStorageInfo = document.getElementById('global-storage-info');
            
            globalShowStorage?.addEventListener('click', () => {
                const savedData = VehicleCacheManager.loadSavedData();
                
                let info = '<strong>📊 Consolidated Storage Information:</strong><br>';
                
                if (savedData) {
                    const date = new Date(savedData.timestamp).toLocaleString();
                    info += `<br>🚗 <strong>Saved Data (${savedData.source}):</strong> ${savedData.makeName || 'Unknown'}`;
                    if (savedData.modelName) info += ` ${savedData.modelName}`;
                    if (savedData.generationName) info += ` (${savedData.generationName})`;
                    
                    if (savedData.plateNumber) {
                        info += `<br>🔢 <strong>Plate:</strong> ${savedData.plateNumber}`;
                        if (savedData.plateResult && savedData.plateResult.vehicle) {
                            info += ` - ${savedData.plateResult.vehicle.make} ${savedData.plateResult.vehicle.model}`;
                        }
                        let selectElement = document.querySelector('.banner_result select');

                        if (selectElement) {
                            selectElement.textContent = 'New text here';
                        }

                    }
                    
                    info += `<br>📅 <strong>Saved:</strong> ${date}`;
                } else {
                    info += '<br>🚗 <strong>Saved Data:</strong> None';
                }
                
                // Show cache info
                const cacheCount = Object.keys(VehicleCacheManager.memoryCache).length;
                info += `<br><br>💾 <strong>Memory Cache:</strong> ${cacheCount} items`;
                
                try {
                    const storageUsed = JSON.stringify(localStorage).length;
                    info += `<br>📊 <strong>Storage Used:</strong> ~${(storageUsed/1024).toFixed(1)}KB`;
                } catch (e) {
                    info += '<br>📊 <strong>Storage:</strong> Unable to calculate size';
                }
                           
                globalStorageInfo.innerHTML = info;
                
                // Hide detailed view after 10 seconds
                setTimeout(() => {
                    VehicleCacheManager.updateGlobalStorageInfo();
                }, 10000);
            });
            
            globalClearStorage?.addEventListener('click', () => {
                if (confirm('Are you sure you want to clear all cached vehicle data and saved selections?')) {
                    VehicleCacheManager.clearAllStorage();
                    
                    // Reset all forms
                    selectors.forEach(selector => {
                        selector.performReset();
                    });
                    
                   // alert('All cached data and saved selections have been cleared.');
                }
            });
            
            // Add button to clear just saved data (not cache)
            const globalClearSelections = document.getElementById('global-clear-selections');
            globalClearSelections?.addEventListener('click', () => {
                if (confirm('Are you sure you want to clear saved data? (This will keep API cache intact)')) {
                    VehicleCacheManager.clearSavedState();
                   // alert('Saved data has been cleared.');
                }
            });
        });

        function initializeVehicleSelectors() {
            // Check if registration elements exist (might be disabled via theme settings)
            const containers = document.querySelectorAll('.menu-reg-form');
            
            if (containers.length === 0) {
                console.log('Vehicle registration is disabled or no registration forms found.');
                return [];
            }
            
            // Clean up any disconnected instances first
            FormSyncManager.instances = FormSyncManager.instances.filter(instance => 
                instance.container && instance.container.isConnected
            );
            
            const selectors = [];
            
            containers.forEach(container => {
                // Only initialize if not already initialized
                if (!container.vehicleSelectorInstance || !container.isConnected) {
                    const selector = new VehicleSelector(container);
                    container.vehicleSelectorInstance = selector;
                    selectors.push(selector);
                }
            });
            
            if (selectors.length > 0) {
                // Trigger restore for newly initialized selectors (debounced)
                FormSyncManager.scheduleRestore(150);
            }
            
            return selectors;
        }

function syncBannerToPlates() {
    let bannerInput = document.querySelector('.homepagereg .banner_reg_input');
    let plateInputs = document.querySelectorAll('.plate-input');
    let form = document.querySelector('.banner_reg_submit');
    let plateLookupBtn = document.querySelector('.btn-plate-lookup');

    // Only initialize if registration elements exist
    if (!bannerInput && plateInputs.length === 0) {
        return;
    }

    if (bannerInput && plateInputs.length > 0) {
        bannerInput.addEventListener('input', () => {
            plateInputs.forEach(input => {
                input.value = bannerInput.value;
            });
        });
    }

   if (form && plateLookupBtn) {
    form.addEventListener('click', (e) => {
        e.preventDefault();
        let homepagereg = form.closest('.homepagereg') || document.querySelector('.homepagereg');
        let bannerInput = homepagereg ? homepagereg.querySelector('.banner_reg_input') : null;
        let regVal = bannerInput ? bannerInput.value.trim().toUpperCase() : '';
        if (regVal.length < 4) return;
        var menuForm = plateLookupBtn.closest('.menu-reg-form');
        if (menuForm) menuForm.classList.add('is-loading');
        if (form.disabled !== undefined) form.disabled = true;
        form.setAttribute('aria-busy', 'true');
        plateLookupBtn.click();
    });
}
document.addEventListener('registration-lookup-complete', function () {
    document.querySelectorAll('.menu-reg-form').forEach(function (el) {
        el.classList.remove('is-loading');
    });
    var submitBtn = document.querySelector('.banner_reg_submit');
    if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
    }
});

}

syncBannerToPlates();


window.addEventListener('updated.ecomify.collection', initializeVehicleSelectors)


document.addEventListener('click', function(e) {
    if (e.target.classList.contains('check-fitment')) {
        e.preventDefault();
        let navMenu = document.querySelector('#navbar-desktop');
        if (navMenu) {
            let relatedLinks = navMenu.querySelector('.related-links');
            if (relatedLinks) {
                let firstLink = relatedLinks.querySelector('.link-item');
                if (firstLink && firstLink.href) {
                   // window.location.href = '/collections/all';
                    window.location.href = firstLink.href;
                }
            }
        }
    }
});

document.addEventListener('click', function(e) {
    if (e.target.classList.contains('reg-go-btn') ) {
        e.preventDefault();
        let navMenu = document.querySelector('#navbar-desktop');
        if (navMenu) {
            let relatedLinks = navMenu.querySelector('.related-links');
            if (relatedLinks) {
                let firstLink = relatedLinks.querySelector('.link-item');
                if (firstLink && firstLink.href) {
                    window.location.href = firstLink.href;
                }
            }
        }
    }
});


document.addEventListener('click', function(e) {
    if (document.body.classList.contains('page-type-article')) {
        if (e.target.classList.contains('btn-plate-lookup')) {
            let regCtaMini = e.target.closest('.reg-cta-mini');
            if (regCtaMini) {
               setTimeout(function() {
                 let firstLink = regCtaMini.querySelector('.link-item');
                    if (firstLink) {
                        let url = firstLink.getAttribute('href');
                        if (url) {
                            window.location.href = url;
                        }
                    }
                }, 2000); 
            }
        }
    }
});

document.addEventListener('click', function(e) {
  if (!document.body.classList.contains('page-type-index')) return;
  let btn = e.target.closest('.banner_reg_submit');
  if (!btn) return;
  if (btn.dataset.waiting === '1') return;
  btn.dataset.waiting = '1';

  let navbar = document.querySelector('#navbar-desktop');
  let linksGrid = navbar ? navbar.querySelector('.links-grid') : document.querySelector('.links-grid');
  let observerTarget = linksGrid || navbar || document.body;

  function getItems() {
    if (linksGrid) return linksGrid.querySelectorAll('.link-item');
    if (navbar) return navbar.querySelectorAll('.links-grid .link-item');
    return document.querySelectorAll('#navbar-desktop .links-grid .link-item');
  }
  function getAnchorFromItem(item) {
    return item.querySelector('a') || item;
  }
  function isValidHref(href) {
    if (!href) return false;
    href = href.trim();
    if (!href) return false;
    if (href === '#' || href.toLowerCase().startsWith('javascript:')) return false;
    return true;
  }

  let stabilityTimer;
  let fallbackTimer;
  let start = Date.now();
  let maxWait = 8000;
  let stabilityMs = 350;
  let observer = null;

  function cleanup() {
    if (observer) observer.disconnect();
    clearTimeout(stabilityTimer);
    clearTimeout(fallbackTimer);
    btn.dataset.waiting = '';
  }

 function doRedirect(url) {
  if (!url) { cleanup(); return; }

 
  let win = window.open('', '_self');

  let hasBannerResult = document.querySelector('.homepagereg .banner_result');
  if (hasBannerResult) {
        setTimeout(function() {
    win.location = url;
      }, 2000);
  } else {
    setTimeout(function() {
      win.location = url; // Safari-safe
    }, 2000);
  }

  cleanup();
}


  function evaluateAndMaybeRedirect(force) {
    let items = Array.from(getItems());
    if (items.length === 0) return false;
    let anchors = items.map(getAnchorFromItem).filter(Boolean);
    let validAnchors = anchors.filter(function(a){ return isValidHref(a.getAttribute('href')); });
    if (!force) {
      if (validAnchors.length === anchors.length && anchors.length > 0) {
        clearTimeout(stabilityTimer);
        stabilityTimer = setTimeout(function(){
          doRedirect(validAnchors[0].getAttribute('href'));
        }, stabilityMs);
        return true;
      }
      return false;
    } else {
      if (validAnchors.length > 0) {
        doRedirect(validAnchors[0].getAttribute('href'));
        return true;
      }
      cleanup();
      return false;
    }
  }

  observer = new MutationObserver(function() {
    clearTimeout(stabilityTimer);
    evaluateAndMaybeRedirect(false);
  });

  observer.observe(observerTarget, { childList: true, subtree: true, attributes: true });

  if (!evaluateAndMaybeRedirect(false)) {
    fallbackTimer = setTimeout(function(){
      evaluateAndMaybeRedirect(true);
    }, maxWait);
  }
});


   let bodyKitBuilder = document.querySelectorAll('.body-kit-builder, .body-kit-builder-block,.body-kit-builder-');
   let bodyKitBuilderUrl = '/collections/all?view=bundle';

   if (bodyKitBuilder.length > 0) {
       bodyKitBuilder.forEach(function(block) {
         if (block.hasAttribute('href')) {
            block.setAttribute('href', bodyKitBuilderUrl);
        }
           let links = block.querySelectorAll('[href]');           
           links.forEach(function(link) {
               link.setAttribute('href', bodyKitBuilderUrl);
           });
       });
   }